import { Directive, HostListener } from '@angular/core';
import { RoundOutcomeMeta } from '../../../interfaces/mini-game.interface';

/** True when the browser is genuinely in fullscreen (cross-vendor). */
export function isBrowserFullscreen(): boolean {
  const doc = document as any;
  return !!(
    doc.fullscreenElement
    || doc.webkitFullscreenElement
    || doc.mozFullScreenElement
    || doc.msFullscreenElement
  );
}

/** Best-effort request for browser fullscreen. Must be called from a user
 *  gesture (e.g. a click handler) or the browser will ignore it. */
export function requestBrowserFullscreen(): void {
  const el = document.documentElement as any;
  const req: any = el.requestFullscreen
    || el.webkitRequestFullscreen
    || el.mozRequestFullScreen
    || el.msRequestFullscreen;
  if (typeof req === 'function') {
    try { req.call(el); } catch { /* gesture / permission denied */ }
  }
}

/** Best-effort exit from browser fullscreen. No-op if not fullscreen. */
export function exitBrowserFullscreen(): void {
  if (!isBrowserFullscreen()) return;
  const doc = document as any;
  const exit: any = doc.exitFullscreen
    || doc.webkitExitFullscreen
    || doc.mozCancelFullScreen
    || doc.msExitFullscreen;
  if (typeof exit === 'function') {
    try { exit.call(document); } catch { /* ignore */ }
  }
}

/**
 * Shared browser-fullscreen control for the Academic Skill Games.
 *
 * The games always render on their own full-viewport dark backdrop (see each
 * shell's SCSS). This base drives *real* browser fullscreen via the
 * `<app-fullscreen-toggle>` button and keeps `isFullscreen` honest.
 *
 * Crucially, `isFullscreen` is seeded from the *current* browser state at
 * construction — so when the user picks a new game while already fullscreen,
 * the freshly-mounted shell shows "Minimize" (not "Fullscreen") and its toggle
 * exits correctly. The `fullscreenchange` listener only fires on transitions,
 * which is why the seed is needed.
 */
@Directive()
export abstract class ImmersiveGameBase {
  /** Mirrors the real browser fullscreen state, seeded on construction. */
  isFullscreen = isBrowserFullscreen();

  toggleFullscreen(): void {
    if (this.isFullscreen) {
      exitBrowserFullscreen();
    } else {
      requestBrowserFullscreen();
    }
  }

  /** Keep the flag (and thus the button label) in sync with the real state,
   *  including when the user presses Esc / F11 outside the button. */
  @HostListener('document:fullscreenchange')
  @HostListener('document:webkitfullscreenchange')
  onFullscreenChange(): void {
    this.isFullscreen = isBrowserFullscreen();
  }

  // ── Per-round skill-analytics timing/tries (shared by every game) ──────
  //
  // Captured here once so every shell reports reaction time, time-to-correct
  // and failed tries the same way (older shells did this ad hoc, or not at
  // all). Feed `roundMeta()` into `score<Game>Round(...)`; the service stamps
  // it onto the shared `gameWorld-round` event. See ig-game-theme.md §3.

  protected roundStartedAt = 0;
  protected firstInteractionAt: number | null = null;
  protected failedTries = 0;

  /** Start the round clock — call when the board first becomes interactive
   *  (e.g. after the turn-intro card is dismissed / on a fresh round). */
  protected beginRoundTiming(): void {
    this.roundStartedAt = Date.now();
    this.firstInteractionAt = null;
    this.failedTries = 0;
  }

  /** Record the student's first interaction/submission of the round. */
  protected markInteraction(): void {
    if (this.firstInteractionAt == null) this.firstInteractionAt = Date.now();
  }

  /** Record a wrong submission (also counts as the first interaction). */
  protected markFailedTry(): void {
    this.markInteraction();
    this.failedTries++;
  }

  /** ms from round shown to first interaction (null if never touched). */
  protected get reactionTimeMs(): number | null {
    return this.firstInteractionAt == null || !this.roundStartedAt
      ? null
      : this.firstInteractionAt - this.roundStartedAt;
  }

  /** The round-outcome bundle passed to `score<Game>Round`. `timeToCorrectMs`
   *  is only meaningful on a solve — null on a miss/skip. */
  protected roundMeta(solved: boolean): RoundOutcomeMeta {
    return {
      failedTries: this.failedTries,
      reactionTimeMs: this.reactionTimeMs,
      timeToCorrectMs: solved && this.roundStartedAt ? Date.now() - this.roundStartedAt : null
    };
  }
}
